import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Fade,
} from '@mui/material'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import SearchIcon from '@mui/icons-material/Search'
import AccessTimeIcon from '@mui/icons-material/AccessTime'

const MESSAGES = [
  "Hold tight! We're processing your file",
  'Analyzing contact phone numbers & formatting',
  'Verifying template variables & mapped columns',
  'Checking for duplicate entries',
]
const MESSAGE_INTERVAL_MS = 5000

interface ValidateCsvLoadingDialogProps {
  open: boolean
}

const ValidateCsvLoadingDialog: React.FC<ValidateCsvLoadingDialogProps> = ({ open }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [msgIndex, setMsgIndex] = useState(0)

  useEffect(() => {
    if (!open) {
      setMsgIndex(0)
      return
    }
    const timer = setInterval(() => {
      setMsgIndex((prev) => (prev + 1) % MESSAGES.length)
    }, MESSAGE_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [open])

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.25)',
          position: 'relative',
          m: isMobile ? 2 : undefined,
          width: isMobile ? 'calc(100% - 32px)' : undefined,
        },
      }}
    >
      {/* Animated Top Shimmer Progress Bar */}
      <Box
        sx={{ height: 4, width: '100%' }}
      />

      <DialogContent sx={{ p: isMobile ? 3 : 4, textAlign: 'center' }}>
        {/* Header Title */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: isMobile ? '1.15rem' : '1.3rem',
            color: 'text.primary',
            letterSpacing: '-0.02em',
            mb: 0.5,
          }}
        >
          Validating Audience CSV
        </Typography>

        {/* Central High-Tech Scanner Container */}
        <Box
          sx={{
            my: 3,
            py: 3,
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {/* Radial Ambient Glow */}
          <Box
            sx={{
              position: 'absolute',
              width: 140,
              height: 140,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(25, 118, 210, 0.15) 0%, rgba(255, 255, 255, 0) 70%)',
              animation: 'pulseGlow 3s ease-in-out infinite alternate',
              '@keyframes pulseGlow': {
                '0%': { transform: 'scale(0.85)', opacity: 0.5 },
                '100%': { transform: 'scale(1.15)', opacity: 1 },
              },
            }}
          />

          {/* Document Sheet Card */}
          <Box
            sx={{
              width: 90,
              height: 110,
              bgcolor: '#ffffff',
              borderRadius: '10px',
              border: '1.5px solid #E2E8F0',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.08)',
              position: 'relative',
              overflow: 'hidden',
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              animation: 'docFloat 3s ease-in-out infinite',
              '@keyframes docFloat': {
                '0%, 100%': { transform: 'translateY(0)' },
                '50%': { transform: 'translateY(-4px)' },
              },
            }}
          >
            {/* CSV Badge Header inside Card */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box
                sx={{
                  px: 0.75,
                  py: 0.2,
                  bgcolor: '#1976d2',
                  color: '#fff',
                  borderRadius: '4px',
                  fontWeight: 800,
                  fontSize: '0.6rem',
                  letterSpacing: '0.5px',
                }}
              >
                CSV
              </Box>
              <InsertDriveFileOutlinedIcon sx={{ fontSize: 14, color: '#94A3B8' }} />
            </Box>

            {/* Simulated CSV Table Rows */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Box sx={{ height: 6, width: '40%', bgcolor: '#E2E8F0', borderRadius: 1 }} />
                <Box sx={{ height: 6, width: '55%', bgcolor: '#CBD5E1', borderRadius: 1 }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Box sx={{ height: 6, width: '60%', bgcolor: '#E2E8F0', borderRadius: 1 }} />
                <Box sx={{ height: 6, width: '35%', bgcolor: '#E2E8F0', borderRadius: 1 }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Box sx={{ height: 6, width: '30%', bgcolor: '#CBD5E1', borderRadius: 1 }} />
                <Box sx={{ height: 6, width: '65%', bgcolor: '#E2E8F0', borderRadius: 1 }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Box sx={{ height: 6, width: '50%', bgcolor: '#E2E8F0', borderRadius: 1 }} />
                <Box sx={{ height: 6, width: '45%', bgcolor: '#CBD5E1', borderRadius: 1 }} />
              </Box>
            </Box>

            {/* Horizontal Laser Scanning Line */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, transparent, #1976d2, #64B5F6, #1976d2, transparent)',
                boxShadow: '0 0 10px #1976d2, 0 0 4px #64B5F6',
                animation: 'laserScan 2s ease-in-out infinite alternate',
                '@keyframes laserScan': {
                  '0%': { top: '8px' },
                  '100%': { top: '95px' },
                },
              }}
            />
          </Box>

          {/* Floating Target Lock / Magnifying Lens */}
          <Box
            sx={{
              position: 'absolute',
              animation: 'magnifierOrbit 3.2s ease-in-out infinite',
              '@keyframes magnifierOrbit': {
                '0%': { transform: 'translate(-26px, -24px) rotate(-12deg) scale(1)' },
                '25%': { transform: 'translate(24px, -18px) rotate(10deg) scale(1.08)' },
                '50%': { transform: 'translate(18px, 24px) rotate(-8deg) scale(1)' },
                '75%': { transform: 'translate(-20px, 18px) rotate(12deg) scale(1.05)' },
                '100%': { transform: 'translate(-26px, -24px) rotate(-12deg) scale(1)' },
              },
            }}
          >
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(4px)',
                border: '2px solid #1976d2',
                boxShadow: '0 4px 14px rgba(25, 118, 210, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchIcon sx={{ fontSize: 22, color: '#1976d2' }} />
            </Box>
          </Box>
        </Box>

        {/* Rotating Dynamic Status Message */}
        <Box sx={{ minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Fade key={msgIndex} in timeout={500}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: isMobile ? '0.85rem' : '0.925rem',
                px: 1,
                lineHeight: 1.4,
              }}
            >
              {MESSAGES[msgIndex]}
            </Typography>
          </Fade>
        </Box>

        {/* Bottom Helper Note Tag */}
        <Box
          sx={{
            mt: 2.5,
            pt: 2,
            borderTop: '1px solid #F1F5F9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 15, color: '#64748B' }} />
          <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500, fontSize: '0.75rem' }}>
            Large files may take up to 5 minutes
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default ValidateCsvLoadingDialog
